import React, { useRef, useEffect } from "react";
import { Button } from "../ui/button";

export type SortType = "ORDERS" | "RANK" | "AFFILIATE";
export type SortDirection = "asc" | "desc";

interface SortFilterProps {
  onApply: (type: SortType, direction: SortDirection) => void;
  onReset: () => void;
}

export function SortFilter({ onApply, onReset }: SortFilterProps) {
  const [selectedType, setSelectedType] = React.useState<SortType>("RANK");
  const [direction, setDirection] = React.useState<SortDirection>("desc");
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const handleApply = () => {
    onApply(selectedType, direction);
  };

  const handleReset = () => {
    setSelectedType("RANK");
    setDirection("desc");
    onReset();
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        isDropdownOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border p-4 w-[280px]">
      <div className="space-y-4">
        {/* Sort Type Selector - Custom Dropdown */}
        <div>
          <label className="block text-sm font-medium text-muted-foreground mb-1">
            Sort by
          </label>
          <div className="relative" ref={dropdownRef}>
            {/* Custom Select Dropdown Button */}
            <div
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="w-full px-4 py-2 border border-border rounded-lg text-sm text-muted-foreground bg-card cursor-pointer flex justify-between items-center focus:outline-none focus:ring-1 focus:ring-brand-500 shadow-sm"
            >
              <span>{selectedType}</span>
              <svg
                className="h-4 w-4 text-muted-foreground"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </div>

            {/* Dropdown Menu */}
            {isDropdownOpen && (
              <div className="absolute z-10 w-full mt-1 bg-card border border-border rounded-lg shadow-lg overflow-hidden">
                <div className="py-1">
                  {["RANK", "ORDERS", "AFFILIATE"].map((type) => (
                    <div
                      key={type}
                      className={`px-4 py-2 hover:bg-accent cursor-pointer text-sm ${
                        selectedType === type
                          ? "bg-brand-50 text-brand-700"
                          : "text-muted-foreground"
                      }`}
                      onClick={() => {
                        setSelectedType(type as SortType);
                        setIsDropdownOpen(false);
                      }}
                    >
                      {type}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sort Direction */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-muted-foreground">
            Direction
          </label>
          <div className="flex items-center gap-4">
            <label className="inline-flex items-center">
              <input
                type="radio"
                className="form-radio text-brand-600 focus:ring-brand-500"
                name="sortDirection"
                checked={direction === "desc"}
                onChange={() => setDirection("desc")}
              />
              <span className="ml-2 text-sm text-muted-foreground">High to Low</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                className="form-radio text-brand-600 focus:ring-brand-500"
                name="sortDirection"
                checked={direction === "asc"}
                onChange={() => setDirection("asc")}
              />
              <span className="ml-2 text-sm text-muted-foreground">Low to High</span>
            </label>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-2 border-t border-border">
          <button
            onClick={handleReset}
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            Reset
          </button>
          <Button onClick={handleApply}>Apply</Button>
        </div>
      </div>
    </div>
  );
}
